"use client"

import { useEffect, useRef } from "react"
import * as THREE from "three"
import Image from "next/image"

export default function CarbonHero() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    if (!canvasRef.current) return

    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(70, window.innerWidth / 500, 0.1, 1000)
    camera.position.z = 4.5

    const renderer = new THREE.WebGLRenderer({
      canvas: canvasRef.current,
      alpha: true,
      antialias: true,
    })

    renderer.setSize(window.innerWidth, 500)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    const particlesGeometry = new THREE.BufferGeometry()
    const particlesCount = 3000
    const posArray = new Float32Array(particlesCount * 3)

    for (let i = 0; i < particlesCount * 3; i++) {
      posArray[i] = (Math.random() - 0.5) * 7
    }

    particlesGeometry.setAttribute("position", new THREE.BufferAttribute(posArray, 3))

    const getStarTexture = () => {
      const canvas = document.createElement("canvas")
      canvas.width = 32
      canvas.height = 32
      const ctx = canvas.getContext("2d")
      if (!ctx) return null

      const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16)
      gradient.addColorStop(0, "rgba(255, 255, 255, 1)")
      gradient.addColorStop(0.2, "rgba(255, 255, 255, 0.8)")
      gradient.addColorStop(0.5, "rgba(255, 255, 255, 0.2)")
      gradient.addColorStop(1, "rgba(255, 255, 255, 0)")

      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, 32, 32)

      const texture = new THREE.CanvasTexture(canvas)
      return texture
    }

    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.04,
      map: getStarTexture(),
      transparent: true,
      opacity: 0.6,
      color: 0xffffff,
      depthWrite: false, // Ensures transparency works correctly when overlapping
      blending: THREE.AdditiveBlending, // Makes stars glow when overlapping
    })

    const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial)
    scene.add(particlesMesh)

    const animate = () => {
      particlesMesh.rotation.y += 0.0003
      particlesMesh.rotation.x += 0.00012
      renderer.render(scene, camera)
      requestAnimationFrame(animate)
    }
    animate()

    const handleResize = () => {
      camera.aspect = window.innerWidth / 500
      camera.updateProjectionMatrix()
      renderer.setSize(window.innerWidth, 500)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return (
    <section className="relative min-h-[450px] lg:min-h-[550px] flex flex-col justify-center bg-[#020617] pt-12 pb-4 lg:pt-14 lg:pb-12 overflow-hidden">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-0 pointer-events-none opacity-40"
      />

      {/* Background Glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] bg-blue-600/20 blur-[120px] rounded-full" />
      </div>

      <div className="max-w-[1800px] mx-auto px-8 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-[25%_50%_25%] items-center gap-8 lg:gap-4">

          {/* LEFT: Heading */}
          <div className="space-y-6 text-center lg:text-left order-2 lg:order-1 relative z-20 lg:-translate-y-20">
            <h1 className="text-2xl sm:text-6xl md:text-7xl font-extrabold leading-tight text-white font-[family-name:var(--font-plus-jakarta-sans)]">
              Leader in{" "}
              <span className="whitespace-nowrap">
                Enterprise AI
              </span>
            </h1>


            <p className="text-xl text-white/70 max-w-[500px] mx-auto lg:mx-0 leading-relaxed font-[family-name:var(--font-plus-jakarta-sans)]">
              Transforming enterprises through autonomous AI systems, cognitive
              architectures, and next-generation artificial intelligence solutions.
            </p>
          </div>

          {/* CENTER: Image */}
          <div data-nosnippet="true" className="relative flex justify-center order-1 lg:order-2 z-10 lg:-ml-40">
            <div className="relative w-[320px] md:w-[420px] lg:w-[700px] aspect-[4/5]">
              {/* Background gradient removed as per request */}



              <Image
                src="/images/Hero Section with Rakesh.png"
                alt="Rakesh Gohel"
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                priority
                className="object-cover relative z-10"
                style={{
                  maskImage:
                    "radial-gradient(ellipse at 50% 35%, black 25%, transparent 75%)",
                  WebkitMaskImage:
                    "radial-gradient(ellipse at 50% 35%, black 25%, transparent 75%)",
                }}
              />
            </div>
          </div>

          {/* RIGHT: Expertise */}
          <div className="space-y-6 flex flex-col items-center lg:items-center order-3 relative z-20 lg:-translate-x-0 lg:-translate-y-20">
            <div className="space-y-2 flex flex-col items-center lg:items-start">
              <p className="text-white font-bold text-[24px] text-center lg:text-left font-[family-name:var(--font-plus-jakarta-sans)]">
                Expert in
              </p>

              <div className="flex flex-col gap-2">
                {["Agentic AI", "AI Governance", "Data and AI Strategy"].map(
                  (tag) => (
                    <div
                      key={tag}
                      className="flex items-center gap-2.5 px-3 py-1.5 bg-white rounded-full shadow-lg hover:scale-105 transition-transform cursor-default w-fit"
                    >
                      <div className="w-6 h-6 rounded-full bg-[#2563EB]" />
                      <span className="text-[#020617] font-bold text-base whitespace-nowrap font-[family-name:var(--font-plus-jakarta-sans)] pr-1.5">
                        {tag}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Recognized by Heading - Moved from RecognizedBy component */}
        <div className="mt-1 text-center relative z-20">
          <h2 className="text-white text-3xl md:text-3xl font-bold tracking-tight font-[family-name:var(--font-plus-jakarta-sans)]">
            Recognized by
          </h2>
        </div>
      </div>




    </section>
  )
}
